import React, { useState, useRef, useMemo, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Grid,
  LoadingOverlay,
  Select,
  Stack,
  TextInput,
  ScrollArea,
} from "@mantine/core";
import { UPDATE_VEHICLE } from "apollo/mutuations";
import { useForm } from "@mantine/form";
import {
  GET_DRIVERS,
  GET_REGIONS,
  GET_VEHICLE,
  GET_VEHICLE_TYPES,
} from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { showNotification } from "@mantine/notifications";
import { IconUpload } from "@tabler/icons-react";
import { SimpleGrid } from "@mantine/core";
import { MAX_FILE_SIZE } from "utiles/url";

const VehicleEditModal = ({ editId, openedEdit, setOpenedEdit }) => {
  const [updateVehicle, { loading: updateVehicleLoading }] =
    useMutation(UPDATE_VEHICLE);
  const [regionDropDownData, setRegionDropDownData] = useState([]);

  const [vehicleTypeDropDownData, setVehicleTypeDropDownData] = useState([]);

  const form = useForm({});

  const [files, setFiles] = useState({ plate: [], license: [], libre: [] });
  const [defaultFiles, setDefaultFiles] = useState({
    plate: null,
    license: null,
    libre: null,
  });

  const fileInputRefs = {
    plate: useRef(null),
    license: useRef(null),
    libre: useRef(null),
  };

  const handleFileChange = (event, fileType) => {
    const selectedFiles = Array.from(event.target.files || []);
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];

    const validFiles = selectedFiles.filter((file) => {
      if (!allowedTypes.includes(file.type)) {
        showNotification({
          color: "red",
          title: "Error",
          message: `File ${file.name} is not an accepted format. Allowed: JPG, PNG, PDF.`,
        });
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        showNotification({
          color: "red",
          title: "Error",
          message: `File ${file.name} exceeds 2MB. Please upload a smaller file.`,
        });
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setFiles((prevFiles) => ({ ...prevFiles, [fileType]: validFiles }));
      setDefaultFiles((prev) => ({ ...prev, [fileType]: null }));
    }
  };

  const FilePreview = ({ fileList, url }) => {
    const previews = useMemo(() => {
      const items = [];
      if (url) items.push({ src: url, isUrl: true });
      fileList.forEach((file) => {
        const fileUrl = URL.createObjectURL(file);
        items.push({ src: fileUrl, isUrl: false, revoke: true });
      });
      return items;
    }, [fileList, url]);

    return (
      <SimpleGrid
        cols={3}
        spacing="md"
        style={{ maxHeight: "200px", overflowY: "auto" }}
      >
        {previews.map((p, idx) => (
          <div key={idx} style={{ margin: "10px auto" }}>
            {p.src &&
            (p.src.endsWith(".pdf") || p.src.includes("application/pdf")) ? (
              <embed
                src={p.src}
                width="130"
                height="160"
                type="application/pdf"
              />
            ) : (
              <img src={p.src} alt="" width="130" />
            )}
          </div>
        ))}
      </SimpleGrid>
    );
  };

  const { loading, refetch } = useQuery(GET_VEHICLE, {
    variables: { id: editId },
    onCompleted(data) {
      console.log(data);
      form.setValues({
        model: data.vehicle.model,
        owner_name: data.vehicle.owner_name,
        color: data.vehicle.color,
        plate_number: data.vehicle.plate_number,
        owner_phone: data.vehicle.owner_phone,
        libre_number: data.vehicle.libre_number,
        vehicle_type: { connect: parseInt(data.vehicle.vehicle_type.id) },
        driver: { connect: parseInt(data.vehicle.driver.id) },
        region: { connect: parseInt(data.vehicle.region.id) },
      });
      setDefaultFiles({
        plate: data.vehicle.plate_doc || null,
        license: data.vehicle.license_doc || null,
        libre: data.vehicle.libre_doc || null,
      });
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || "Failed to get vehicle",
      });
    },
  });
  useEffect(() => {
    refetch();
  }, []);
  const { loading: vehicle_type_loading } = useQuery(GET_VEHICLE_TYPES, {
    variables: {
      first: 100,
    },
    onCompleted(data) {
      const newArr = [];
      data.vehicleTypes.data.forEach((element) => {
        newArr.push({ label: element.title, value: element.id });
      });

      setVehicleTypeDropDownData(newArr);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to get vehicle types",
      });
    },
  });

  const { loading: region_loading } = useQuery(GET_REGIONS, {
    variables: { first: 100, page: 1 },
    onCompleted(data) {
      const newArr = data.regions.data.map((element) => ({
        label: element.name,
        value: element.id,
      }));
      setRegionDropDownData(newArr);
    },
    onError() {
      showNotification({
        color: "red",
        title: "Error",
        message: "Something went wrong while fetching regions!",
      });
    },
  });

  const submit = () => {
    updateVehicle({
      variables: {
        id: editId,
        model: form.getInputProps("model").value,
        plate_number: form.getInputProps("plate_number").value,
        color: form.getInputProps("color").value,
        owner_name: form.getInputProps("owner_name").value,
        owner_phone: form.getInputProps("owner_phone").value,
        driver: form.getInputProps("driver").value,
        vehicle_type: form.getInputProps("vehicle_type").value,
        region: form.getInputProps("region").value,
        plate_doc: files.plate[0] || null,
        license_doc: files.license[0] || null,
        libre_doc: files.libre[0] || null,
        libre_number: form.getInputProps("libre_number").value,
      },
      onCompleted() {
        showNotification({
          color: "green",
          title: "Success",
          message: "Vehicle Edited Successfully",
        });

        setOpenedEdit(false);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to update vehicle",
        });
      },
    });
  };

  const setVehicleTypeDropDownValue = (val) => {
    form.setFieldValue("vehicle_type.connect", val);
  };

  const setRegionDropDownValue = (val) => {
    form.setFieldValue("region.connect", val);
  };
  return (
    <>
      <LoadingOverlay
        visible={loading || updateVehicleLoading || vehicle_type_loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <form onSubmit={form.onSubmit(() => submit())}>
        <Stack>
          <Grid>
            <Grid.Col span={4}>
              <TextInput
                placeholder="Model"
                label="Model"
                {...form.getInputProps("model")}
                withAsterisk
              />

              <TextInput
                placeholder="Plate Number"
                label="Plate Number"
                {...form.getInputProps("plate_number")}
                withAsterisk
              />

              <TextInput
                placeholder="Color"
                label="Color"
                {...form.getInputProps("color")}
                withAsterisk
              />
              <Select
                label="Select Region"
                searchable
                placeholder="Pick one"
                data={regionDropDownData}
                value={form.getInputProps("region.connect").value?.toString()}
                onChange={setRegionDropDownValue}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <TextInput
                placeholder="Owner Name"
                label="Owner Name"
                {...form.getInputProps("owner_name")}
                withAsterisk
              />
              <TextInput
                placeholder="Owner Phone"
                label="Owner Phone"
                {...form.getInputProps("owner_phone")}
                withAsterisk
              />
              {/* pick vehcle type */}
              <Select
                label="Select Vehicle Type"
                placeholder="Pick one"
                data={vehicleTypeDropDownData}
                value={form
                  .getInputProps("vehicle_type.connect")
                  .value?.toString()}
                onChange={setVehicleTypeDropDownValue}
              />
              <TextInput
                placeholder="Libre Number"
                label="Libre Number"
                {...form.getInputProps("libre_number")}
                withAsterisk
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <div
                style={{
                  display: "flex",
                  marginTop: "20px",
                  justifyContent: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: "1 1 5%", margin: "5px" }}>
                  <Button
                    onClick={() => fileInputRefs.license.current.click()}
                    variant="outline"
                    leftIcon={<IconUpload size={16} />}
                  >
                    Upload License
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRefs.license}
                    accept="image/jpeg,image/png,image/jpg,.pdf"
                    style={{ display: "none" }}
                    onChange={(event) => handleFileChange(event, "license")}
                  />
                  <FilePreview
                    fileList={files.license}
                    url={defaultFiles.license}
                  />
                </div>

                <div style={{ flex: "1 1 5%", margin: "5px" }}>
                  <Button
                    onClick={() => fileInputRefs.libre.current.click()}
                    variant="outline"
                    leftIcon={<IconUpload size={16} />}
                  >
                    Upload Libre
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRefs.libre}
                    accept="image/jpeg,image/png,image/jpg,.pdf"
                    style={{ display: "none" }}
                    onChange={(event) => handleFileChange(event, "libre")}
                  />
                  <FilePreview
                    fileList={files.libre}
                    url={defaultFiles.libre}
                  />
                </div>
              </div>
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={12}>
              <Button
                type="submit"
                style={{
                  marginTop: "20px",
                  width: "20%",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
                fullWidth
                color="blue"
              >
                Submit
              </Button>
            </Grid.Col>
          </Grid>
        </Stack>
      </form>
    </>
  );
};

export default VehicleEditModal;
